import {
    useContext,
    useEffect,
    type FC
} from "react";
import {
    Animated,
    View
} from "react-native";
import type IResizablePanelProps from "./type";
import stylesheet, {
    RESIZABLE_PANEL_SIZE_KEYS,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    ResizableIndexContext,
    ResizableContext
} from "../../context";
import {
    warnOnce
} from "../../../../utils/development";
import {
    getPanelViewProps
} from "../../util";
import Tabs from "../../../tabs";

const ResizablePanel: FC<IResizablePanelProps> = (panelProps) => {
    const {
        isCollapsible = false,
        children,
        style
    } = panelProps;

    const props = getPanelViewProps(panelProps);

    const context = useContext(ResizableContext);

    const index = useContext(ResizableIndexContext);

    const {
        shapeConfigs,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(context?.customTheme);

    const {
        container: containerDynamicStyle
    } = useStyles({
        collapsedSize: context?.collapsedSizes[index] ?? 0,
        orientation: context?.orientation ?? "horizontal",
        isCollapsed: !!context?.collapsed[index],
        panelCount: context?.panelCount ?? 0,
        mode: context?.mode ?? "split",
        isFramed: !!context?.isFramed,
        isCollapsible,
        shapeConfigs,
        radiuses,
        borders,
        colors,
        index
    });

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: "Resizable.Panel: a panel must be placed inside a \"Resizable.PanelGroup\"."
        });
    }, [
        context
    ]);

    if(!context) {
        return <View
            {...props}
            style={style}
        >
            {children}
        </View>;
    }

    if(context.mode === "tabs") {
        return <Tabs.Content
            style={[
                style,
                containerDynamicStyle
            ]}
            value={String(index)}
        >
            {children}
        </Tabs.Content>;
    }

    if(context.mode === "stack") {
        return <View
            {...props}
            style={[
                style,
                stylesheet.stack,
                containerDynamicStyle
            ]}
        >
            {children}
        </View>;
    }

    const sizeStyle = context.isReady ? {
        [RESIZABLE_PANEL_SIZE_KEYS[context.orientation]]: context.animatedSizes[index]
    } : undefined;

    return <Animated.View
        {...props}
        style={[
            style,
            stylesheet.container,
            context.isReady ? stylesheet.measured : stylesheet.unmeasured,
            containerDynamicStyle,
            sizeStyle
        ]}
        id={context.panelIDs[index]}
    >
        {children}
    </Animated.View>;
};
export default Object.assign(ResizablePanel, {
    resizablePart: "panel" as const
});
